import { Button } from 'antd'
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type Simulation
} from 'd3-force'
import { Maximize2, Minus, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  CATEGORY_COLORS,
  EVENT_KIND_COLORS,
  FORCE,
  LEFT_RESERVED,
  PARTICLE_TTL_MS
} from './constants'
import { useStyles } from './styles'
import type { EventKind, GraphEdge, GraphNode } from './types'

type Props = {
  nodes: GraphNode[]
  edges: GraphEdge[]
  alerts: Record<string, EventKind | undefined>
  edgePing: { edgeId: string; at: number } | null
  /** transient node ripple driven by a fresh business event */
  flash: { nodeId: string; at: number } | null
  selectedId: string | null
  selectedEdgeId: string | null
  hoverId: string | null
  reducedMotion: boolean
  onHover: (id: string | null) => void
  onSelect: (id: string | null) => void
  onSelectEdge: (id: string | null) => void
}

const asNode = (end: string | GraphNode): GraphNode => end as GraphNode
const endId = (end: string | GraphNode): string =>
  typeof end === 'string' ? end : end.id
const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v))

/** Curved arc between two points, matching the mockup's gentle bend. */
const arcPath = (x1: number, y1: number, x2: number, y2: number): string => {
  const r = Math.hypot(x2 - x1, y2 - y1) * 1.9

  return `M${x1},${y1}A${r},${r} 0 0,1 ${x2},${y2}`
}

export const CompanyGraphCanvas = ({
  nodes,
  edges,
  alerts,
  edgePing,
  flash,
  selectedId,
  selectedEdgeId,
  hoverId,
  reducedMotion,
  onHover,
  onSelect,
  onSelectEdge
}: Props) => {
  const { styles } = useStyles()
  const stageRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 860, h: 440 })
  const [view, setView] = useState({ k: 1, tx: 0, ty: 0 })

  const nodeElRefs = useRef(new Map<string, SVGGElement | null>())
  const edgeElRefs = useRef(new Map<string, SVGPathElement | null>())
  const edgeHitRefs = useRef(new Map<string, SVGPathElement | null>())
  const simRef = useRef<Simulation<GraphNode, GraphEdge> | null>(null)
  const nodesRef = useRef<GraphNode[]>(nodes)
  const edgesRef = useRef<GraphEdge[]>(edges)
  const particleRef = useRef<SVGCircleElement>(null)
  const rippleRef = useRef<SVGCircleElement>(null)
  const dragRef = useRef<{
    id: string
    moved: boolean
    x0: number
    y0: number
  } | null>(null)
  const panRef = useRef<{ x: number; y: number; moved: boolean } | null>(null)

  useEffect(() => {
    nodesRef.current = nodes
    edgesRef.current = edges
  })

  const activeId = hoverId ?? selectedId
  const neighborIds = useMemo(() => {
    if (!activeId) return null
    const set = new Set<string>([activeId])
    edges.forEach((e) => {
      if (endId(e.source) === activeId) set.add(endId(e.target))
      if (endId(e.target) === activeId) set.add(endId(e.source))
    })

    return set
  }, [activeId, edges])

  const paintPositions = () => {
    edgesRef.current.forEach((e) => {
      const s = asNode(e.source)
      const tg = asNode(e.target)
      if (s?.x == null || tg?.x == null) return
      const d = arcPath(s.x, s.y ?? 0, tg.x, tg.y ?? 0)
      edgeElRefs.current.get(e.id)?.setAttribute('d', d)
      edgeHitRefs.current.get(e.id)?.setAttribute('d', d)
    })
    nodesRef.current.forEach((n) => {
      const el = nodeElRefs.current.get(n.id)
      if (!el || n.x == null) return
      el.setAttribute('transform', `translate(${n.x},${n.y ?? 0})`)
    })
  }

  const topology = useMemo(
    () =>
      `${nodes.map((n) => n.id).join('|')}#${edges.map((e) => e.id).join('|')}`,
    [nodes, edges]
  )

  useEffect(() => {
    const { w, h } = size
    const cx = (w + LEFT_RESERVED) / 2
    nodes.forEach((n, i) => {
      if (n.x == null) {
        if (n.id === 'company') {
          n.x = cx
          n.y = h / 2
        } else {
          const angle = (i / Math.max(1, nodes.length - 1)) * Math.PI * 2
          n.x = cx + Math.cos(angle) * 120
          n.y = h / 2 + Math.sin(angle) * 90
        }
      }
    })

    const sim = forceSimulation<GraphNode, GraphEdge>(nodes)
      .force(
        'link',
        forceLink<GraphNode, GraphEdge>(edges)
          .id((n) => n.id)
          .distance((e) =>
            e.flow ? FORCE.linkDistanceFlow : FORCE.linkDistance
          )
          .strength(FORCE.linkStrength)
      )
      .force('charge', forceManyBody().strength(FORCE.charge))
      .force('center', forceCenter(cx, h / 2))
      .force(
        'collide',
        forceCollide<GraphNode>().radius((n) => n.radius + FORCE.collidePadding)
      )
      .alphaMin(reducedMotion ? FORCE.alphaMinReduced : FORCE.alphaMin)
      .on('tick', paintPositions)

    simRef.current = sim
    paintPositions()

    return () => {
      sim.stop()
      simRef.current = null
    }
  }, [topology, reducedMotion])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return undefined
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect
      if (box) setSize({ w: box.width, h: box.height })
    })
    ro.observe(stage)

    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const sim = simRef.current
    if (!sim) return
    sim.force('center', forceCenter((size.w + LEFT_RESERVED) / 2, size.h / 2))
    sim.alpha(0.4).restart()
  }, [size])

  // Edge particle.
  const [particleEdge, setParticleEdge] = useState<GraphEdge | null>(null)
  useEffect(() => {
    if (!edgePing || reducedMotion) return undefined
    const edge = edgesRef.current.find((e) => e.id === edgePing.edgeId)
    if (!edge) return undefined
    setParticleEdge(edge)
    const start = performance.now()
    let raf = 0
    const step = (now: number) => {
      const tt = Math.min(1, (now - start) / PARTICLE_TTL_MS)
      const s = asNode(edge.source)
      const tg = asNode(edge.target)
      const dot = particleRef.current
      if (dot && s?.x != null && tg?.x != null) {
        dot.setAttribute('cx', String(s.x + (tg.x - s.x) * tt))
        dot.setAttribute(
          'cy',
          String((s.y ?? 0) + ((tg.y ?? 0) - (s.y ?? 0)) * tt)
        )
        dot.style.opacity = String(
          tt < 0.12 ? tt / 0.12 : 1 - (tt - 0.12) / 0.88
        )
      }
      if (tt < 1) raf = requestAnimationFrame(step)
      else setParticleEdge(null)
    }
    raf = requestAnimationFrame(step)

    return () => cancelAnimationFrame(raf)
  }, [edgePing, reducedMotion])

  // Node ripple (feed ↔ graph bridge), DOM/WAAPI driven.
  useEffect(() => {
    if (!flash || reducedMotion) return undefined
    const node = nodesRef.current.find((n) => n.id === flash.nodeId)
    const el = rippleRef.current
    if (!node || !el || node.x == null) return undefined
    el.setAttribute('cx', String(node.x))
    el.setAttribute('cy', String(node.y ?? 0))
    el.setAttribute('r', String(node.radius))
    el.setAttribute('stroke', CATEGORY_COLORS[node.kind])
    const anim = el.animate(
      [
        { transform: 'scale(1)', opacity: 0.6 },
        { transform: 'scale(2.6)', opacity: 0 }
      ],
      { duration: 1000, easing: 'ease-out' }
    )

    return () => anim.cancel()
  }, [flash, reducedMotion])

  /** Pointer (client) → graph coordinates, inverting the current view transform. */
  const toGraph = (clientX: number, clientY: number) => {
    const rect = stageRef.current?.getBoundingClientRect()
    const lx = clientX - (rect?.left ?? 0)
    const ly = clientY - (rect?.top ?? 0)
    const { k, tx, ty } = view

    return { x: (lx - tx) / k, y: (ly - ty) / k }
  }

  const onPointerDownNode = (e: React.PointerEvent, n: GraphNode) => {
    e.stopPropagation()
    dragRef.current = { id: n.id, moved: false, x0: e.clientX, y0: e.clientY }
    n.fx = n.x ?? null
    n.fy = n.y ?? null
  }

  const onPointerDownBg = (e: React.PointerEvent) => {
    panRef.current = { x: e.clientX, y: e.clientY, moved: false }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current
    if (drag) {
      const node = nodesRef.current.find((n) => n.id === drag.id)
      if (!node) return
      if (!drag.moved) {
        // Ignore sub-threshold jitter so a click stays a click.
        if (Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) < 4) return
        drag.moved = true
        simRef.current?.alphaTarget(0.3).restart()
      }
      const g = toGraph(e.clientX, e.clientY)
      node.fx = g.x
      node.fy = g.y

      return
    }
    const pan = panRef.current
    if (pan) {
      const dx = e.clientX - pan.x
      const dy = e.clientY - pan.y
      if (!pan.moved && Math.hypot(dx, dy) > 3) pan.moved = true
      if (pan.moved) {
        setView((v) => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }))
        pan.x = e.clientX
        pan.y = e.clientY
      }
    }
  }

  const onPointerUp = () => {
    const drag = dragRef.current
    if (drag) {
      const node = nodesRef.current.find((n) => n.id === drag.id)
      if (drag.moved) {
        // Pin where dropped — let the owner arrange the graph.
        simRef.current?.alphaTarget(0)
      } else {
        // Plain click (no real movement): release the pin and select the node.
        if (node) {
          node.fx = null
          node.fy = null
        }
        onSelect(drag.id)
        onSelectEdge(null)
      }
      dragRef.current = null

      return
    }
    const pan = panRef.current
    if (pan) {
      if (!pan.moved) {
        onSelect(null)
        onSelectEdge(null)
      }
      panRef.current = null
    }
  }

  // Leaving the stage only ends a pan (node drags use pointer capture).
  const onPointerLeave = () => {
    panRef.current = null
  }

  const zoomBy = (factor: number) =>
    setView((v) => {
      const k = clamp(v.k * factor, 0.45, 2.6)
      const cx = size.w / 2
      const cy = size.h / 2

      return {
        k,
        tx: cx - ((cx - v.tx) / v.k) * k,
        ty: cy - ((cy - v.ty) / v.k) * k
      }
    })

  const resetView = () => {
    setView({ k: 1, tx: 0, ty: 0 })
    nodesRef.current.forEach((n) => {
      n.fx = null
      n.fy = null
    })
    simRef.current?.alpha(0.6).restart()
  }

  const particleColor = particleEdge
    ? CATEGORY_COLORS[asNode(particleEdge.target)?.kind ?? 'company']
    : 'transparent'

  return (
    <div ref={stageRef} className={styles.stage}>
      <svg
        className={styles.svg}
        viewBox={`0 0 ${size.w} ${size.h}`}
        onPointerDown={onPointerDownBg}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        role="img"
        aria-label="Company resource graph"
      >
        <defs>
          <radialGradient id="cg-core-gradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#9a8cff" />
            <stop offset="100%" stopColor="#5b4bd6" />
          </radialGradient>
          <filter id="cg-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g transform={`translate(${view.tx},${view.ty}) scale(${view.k})`}>
          {/* edges */}
          <g>
            {edges.map((e) => {
              const dim = neighborIds
                ? !(
                    endId(e.source) === activeId || endId(e.target) === activeId
                  )
                : false
              const flow = e.flow && !reducedMotion
              const targetKind = asNode(e.target)?.kind
              const isSel = e.id === selectedEdgeId
              const color = e.flow
                ? CATEGORY_COLORS[targetKind ?? 'company']
                : 'var(--ant-color-border)'

              return (
                <g key={e.id}>
                  <path
                    ref={(el) => {
                      edgeElRefs.current.set(e.id, el)
                    }}
                    className={flow ? 'cg-flow' : undefined}
                    fill="none"
                    stroke={isSel ? 'var(--ant-color-primary)' : color}
                    strokeWidth={isSel ? 3 : e.flow ? 1.6 : 1.3}
                    strokeLinecap="round"
                    strokeDasharray={flow ? '2 9' : undefined}
                    style={{
                      opacity: dim ? 0.07 : isSel ? 1 : e.flow ? 0.9 : 0.55,
                      animation: flow
                        ? 'cg-dash 1.1s linear infinite'
                        : undefined,
                      transition: 'opacity 0.2s',
                      pointerEvents: 'none'
                    }}
                  />
                  {/* fat invisible hit target */}
                  <path
                    ref={(el) => {
                      edgeHitRefs.current.set(e.id, el)
                    }}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
                    onPointerDown={(ev) => ev.stopPropagation()}
                    onClick={(ev) => {
                      ev.stopPropagation()
                      onSelectEdge(e.id === selectedEdgeId ? null : e.id)
                      onSelect(null)
                    }}
                  />
                </g>
              )
            })}
          </g>

          {/* edge particle */}
          <circle
            ref={particleRef}
            r={3.2}
            fill={particleColor}
            style={{ opacity: 0, pointerEvents: 'none' }}
            aria-hidden
          />

          {/* business-event ripple */}
          <circle
            ref={rippleRef}
            r={0}
            fill="none"
            strokeWidth={2.5}
            style={{
              opacity: 0,
              pointerEvents: 'none',
              transformBox: 'fill-box',
              transformOrigin: 'center'
            }}
            aria-hidden
          />

          {/* nodes */}
          <g>
            {nodes.map((n) => {
              const dimmed = neighborIds ? !neighborIds.has(n.id) : false
              const alert = alerts[n.id]
              const isCore = n.kind === 'company'

              return (
                <g
                  key={n.id}
                  ref={(el) => {
                    nodeElRefs.current.set(n.id, el)
                  }}
                  style={{
                    cursor: 'pointer',
                    opacity: dimmed ? 0.22 : 1,
                    transition: 'opacity 0.2s'
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={n.label}
                  onPointerDown={(e) => onPointerDownNode(e, n)}
                  onPointerEnter={() => onHover(n.id)}
                  onPointerLeave={() => onHover(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelect(n.id === selectedId ? null : n.id)
                      onSelectEdge(null)
                    }
                  }}
                  onFocus={() => onHover(n.id)}
                  onBlur={() => onHover(null)}
                >
                  {(hoverId === n.id || selectedId === n.id) && (
                    <circle
                      r={n.radius + 7}
                      fill="none"
                      stroke={CATEGORY_COLORS[n.kind]}
                      strokeWidth={2}
                      opacity={0.55}
                    />
                  )}
                  {alert && (
                    <circle
                      r={n.radius + 4}
                      fill="none"
                      stroke={EVENT_KIND_COLORS[alert]}
                      strokeWidth={2.4}
                      opacity={0.9}
                    />
                  )}
                  {!reducedMotion && (
                    <circle
                      className="cg-pulse"
                      r={n.radius + 4}
                      fill={CATEGORY_COLORS[n.kind]}
                      style={{
                        animation: 'cg-pulse 3.4s ease-in-out infinite'
                      }}
                    />
                  )}
                  <circle
                    r={n.radius}
                    fill={
                      isCore
                        ? 'url(#cg-core-gradient)'
                        : CATEGORY_COLORS[n.kind]
                    }
                    filter={isCore ? 'url(#cg-glow)' : undefined}
                  />
                  <text
                    y={n.radius + 11}
                    textAnchor="middle"
                    fontSize={isCore ? 10 : 9}
                    fontWeight={isCore ? 600 : 500}
                    fill="var(--ant-color-text)"
                    style={{
                      paintOrder: 'stroke',
                      stroke: 'var(--ant-color-bg-container)',
                      strokeWidth: 2.6,
                      pointerEvents: 'none'
                    }}
                  >
                    {n.label}
                  </text>
                </g>
              )
            })}
          </g>
        </g>
      </svg>

      <div className={styles.zoomControls}>
        <Button
          size="small"
          shape="circle"
          icon={<Plus size={14} />}
          onClick={() => zoomBy(1.25)}
          aria-label="Zoom in"
        />
        <Button
          size="small"
          shape="circle"
          icon={<Minus size={14} />}
          onClick={() => zoomBy(0.8)}
          aria-label="Zoom out"
        />
        <Button
          size="small"
          shape="circle"
          icon={<Maximize2 size={13} />}
          onClick={resetView}
          aria-label="Reset view"
        />
      </div>
    </div>
  )
}
